import type { ExpenseDetailDto, UpdateExpenseInput } from '@armard/shared';

export interface ExpenseDraft {
  supplierId: string | null;
  categoryId: string | null;
  supplierInvoiceNumber: string | null;
  customerReference: string | null;
  invoiceDate?: string;
  dueDate: string | null;
  paymentDate: string | null;
  reverseCharge: boolean;
  supplierKleinunternehmer: boolean;
  isKleinbetragsrechnung: boolean;
  privateUseOverridePercent: number | null;
  privateUseReason: string | null;
  notes: string | null;
  internalNotes: string | null;
  items: Array<{
    position: number;
    description: string;
    quantity: number;
    unit: string | null;
    unitPriceNet: number;
    vatRateAtSave: number;
  }>;
}
export function expenseDraft(initial?: Partial<ExpenseDetailDto>): ExpenseDraft {
  return {
    supplierId: initial?.supplierId ?? null,
    categoryId: initial?.categoryId ?? null,
    supplierInvoiceNumber: initial?.supplierInvoiceNumber ?? null,
    customerReference: initial?.customerReference ?? null,
    invoiceDate: initial
      ? (initial.invoiceDate ?? undefined)
      : new Date().toISOString().slice(0, 10),
    dueDate: initial?.dueDate ?? null,
    paymentDate: initial?.paymentDate ?? null,
    reverseCharge: initial?.reverseCharge ?? false,
    supplierKleinunternehmer: initial?.supplierKleinunternehmer ?? false,
    isKleinbetragsrechnung: initial?.isKleinbetragsrechnung ?? false,
    privateUseOverridePercent:
      initial?.privateUseOverridePercent == null ? null : Number(initial.privateUseOverridePercent),
    privateUseReason: initial?.privateUseReason ?? null,
    notes: initial?.notes ?? null,
    internalNotes: initial?.internalNotes ?? null,
    items: initial
      ? (initial.items ?? []).map((item) => ({
          position: item.position,
          description: item.description,
          quantity: Number(item.quantity),
          unit: item.unit,
          unitPriceNet: Number(item.unitPriceNet),
          vatRateAtSave: Number(item.vatRateAtSave),
        }))
      : [
          {
            position: 1,
            description: '',
            quantity: 1,
            unit: null,
            unitPriceNet: 0,
            vatRateAtSave: 19,
          },
        ],
  };
}

/**
 * Reconcile a save acknowledgement with the latest local draft. Fields the
 * user has not touched since the submitted snapshot adopt the server's
 * canonical values (including decimal rounding); newer edits remain local
 * and are sent in the next versioned write.
 */
export function reconcileExpenseDraft(
  submitted: ExpenseDraft,
  latest: ExpenseDraft,
  saved: ExpenseDetailDto
): ExpenseDraft {
  const canonical = expenseDraft(saved);
  function value<K extends keyof ExpenseDraft>(key: K): ExpenseDraft[K] {
    return JSON.stringify(latest[key]) === JSON.stringify(submitted[key])
      ? canonical[key]
      : latest[key];
  }
  const itemFields = ['description', 'quantity', 'unit', 'unitPriceNet', 'vatRateAtSave'] as const;
  const sameItemStructure = (left: ExpenseDraft['items'], right: ExpenseDraft['items']) =>
    left.length === right.length &&
    left.every((item, index) => item.position === right[index].position);
  let reconciledItems = value('items');
  if (
    sameItemStructure(submitted.items, latest.items) &&
    sameItemStructure(submitted.items, canonical.items)
  ) {
    reconciledItems = latest.items.map((row, index) => {
      function itemValue<K extends (typeof itemFields)[number]>(key: K): typeof row[K] {
        return JSON.stringify(row[key]) === JSON.stringify(submitted.items[index][key])
          ? canonical.items[index][key]
          : row[key];
      }
      return {
        ...row,
        description: itemValue('description'),
        quantity: itemValue('quantity'),
        unit: itemValue('unit'),
        unitPriceNet: itemValue('unitPriceNet'),
        vatRateAtSave: itemValue('vatRateAtSave'),
      };
    });
  }

  return {
    supplierId: value('supplierId'),
    categoryId: value('categoryId'),
    supplierInvoiceNumber: value('supplierInvoiceNumber'),
    customerReference: value('customerReference'),
    invoiceDate: value('invoiceDate'),
    dueDate: value('dueDate'),
    paymentDate: value('paymentDate'),
    reverseCharge: value('reverseCharge'),
    supplierKleinunternehmer: value('supplierKleinunternehmer'),
    isKleinbetragsrechnung: value('isKleinbetragsrechnung'),
    privateUseOverridePercent: value('privateUseOverridePercent'),
    privateUseReason: value('privateUseReason'),
    notes: value('notes'),
    internalNotes: value('internalNotes'),
    // Reconcile row fields independently only while row count/order/positions
    // are unchanged. Structural edits keep the latest full array intact.
    items: reconciledItems,
  };
}

export function expenseDraftError(draft: ExpenseDraft, mode: 'create' | 'edit' = 'edit') {
  if (mode === 'create' && !draft.invoiceDate) return 'Bitte ein Rechnungsdatum eintragen.';
  if (draft.invoiceDate === '') return 'Ein vorhandenes Rechnungsdatum kann nicht entfernt werden.';
  if (draft.privateUseOverridePercent != null && (draft.privateUseReason?.trim().length ?? 0) < 10)
    return 'Bitte die abweichende Absetzbarkeit mit mindestens 10 Zeichen begründen.';
  if (
    draft.items.some(
      (item) =>
        !item.description.trim() ||
        !Number.isFinite(item.quantity) ||
        item.quantity <= 0 ||
        !Number.isFinite(item.unitPriceNet) ||
        !Number.isFinite(item.vatRateAtSave) ||
        item.vatRateAtSave < 0 ||
        item.vatRateAtSave > 30
    )
  )
    return 'Bitte jede Position mit Beschreibung, positiver Menge, Preis und Steuersatz von 0 bis 30 % vervollständigen.';
  return null;
}
/** Send only changes; unrelated saves must retain existing items and tax snapshots. */
export function expensePatch(
  expense: ExpenseDetailDto,
  draft: ExpenseDraft
): UpdateExpenseInput | null {
  const baseline = expenseDraft(expense);
  const patch: Record<string, unknown> = { version: expense.version };
  for (const key of Object.keys(draft) as Array<keyof ExpenseDraft>) {
    if (JSON.stringify(draft[key]) !== JSON.stringify(baseline[key])) patch[key] = draft[key];
  }
  return Object.keys(patch).length > 1 ? (patch as UpdateExpenseInput) : null;
}
